<script setup lang="ts">
import {useAuthStore} from "@stores/auth";
import {useAlert} from "@stores/alert";
import ApiService from "@services/ApiService";

const {showAlert} = useAlert();
const authStore = useAuthStore();
const emit = defineEmits(["completed"]);

const documentModel = ref("");
const isLoading = ref(false);
const errors = ref("");

function closeModal() {
  authStore.toggleUserDocumentModal();
  documentModel.value = "";
  errors.value = "";
  isLoading.value = false;
}

function cleanErrors() {
  if (errors.value) {
    errors.value = "";
  }
}

function handleBackdropClick(event: Event) {
  if (event.target === event.currentTarget) {
    closeModal();
  }
}

async function submit() {
  if (!documentModel.value) {
    errors.value = "Digite um CPF válido";
    return;
  }

  try {
    isLoading.value = true;
    errors.value = "";

    const {data} = await ApiService.post("/api/validate/document", {
      document: documentModel.value,
    });

    if (data.data?.errors) {
      errors.value = "Digite um CPF válido";
      isLoading.value = false;
      return;
    }

    const response = await ApiService.post("/api/auth/document", {
      document: documentModel.value,
    });

    if (response.data.success) {
      await authStore.getUser();
      showAlert('success', 'CPF adicionado com sucesso!');
      
      setTimeout(() => {
        closeModal();
        emit("completed");
      }, 1500);
    } else {
      errors.value = response.data.message || 'Erro ao adicionar CPF';
    }
  } catch (error) {
    console.error('Error submitting document:', error);
    errors.value = 'Erro inesperado ao adicionar CPF';
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div v-if="authStore.isUserDocumentModalOpen" class="fixed inset-0 z-[1000] flex items-center justify-center">
    <!-- Backdrop -->
    <div 
      class="fixed inset-0 bg-black/80 transition-opacity"
      @click="handleBackdropClick"
    ></div>
    
    <!-- Modal -->
    <div class="relative w-full max-w-md transform overflow-hidden rounded-md bg-secondary p-6 text-left align-middle shadow-xl transition-all">
      <div class="mb-8 flex flex-col justify-between">
        <h3 class="text-lg font-medium leading-6 text-white">
          Adicione seu documento
        </h3>
        <p class="text-xs text-gray-400">
          É necessário adicionar seu CPF para depositar
        </p>
      </div>

      <button
        @click="closeModal"
        class="absolute right-3 top-3 flex items-center justify-center text-lg text-brand-primary/50 transition-all hover:text-brand-primary"
      >
        <Icon name="fa6-solid:circle-xmark"/>
      </button>

      <div class="mt-2">
        <div class="mt-3 flex items-stretch gap-2">
          <input
            type="text"
            name="user-document"
            class="flex-1 w-full rounded-md bg-brand-input p-4 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="CPF"
            v-model="documentModel"
            v-maska="{ eager: true }"
            data-maska="###.###.###-##"
            @keydown.enter="submit()"
            @input="cleanErrors()"
          />
          <button
            class="min-h-[52px] min-w-[90px] self-start bg-brand-primary text-white rounded-md px-4 py-2 font-medium hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLoading"
            @click="submit()"
          >
            <span v-if="!isLoading">Adicionar</span>
            <span v-else>Carregando...</span>
          </button>
        </div>
        
        <div v-if="errors" class="text-red-500 text-xs mt-2">
          {{ errors }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
